import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Trash2,
  CheckCircle,
  AlertTriangle,
  Shield,
  Archive,
  Database,
  Folder,
} from "lucide-react";
import { ProjectAPI, Project } from "@/api/projects";

interface DeleteProjectProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  onSuccess?: () => void;
}

export function DeleteProject({ isOpen, onClose, project, onSuccess }: DeleteProjectProps) {

  const [isDeleting, setIsDeleting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const handleDelete = async () => {
    if (!project) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      const response = await ProjectAPI.delete(project.id);

      if (response.code === '200') {
        setIsDeleting(false);
        setIsComplete(true);
        
        if (onSuccess) {
          onSuccess();
        }
        
        onClose();
          setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to delete project');
      }
    } catch (error) {
      setIsDeleting(false);
      setError(error instanceof Error ? error.message : 'Failed to delete project. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Project Deleted Successfully!</h3>
          <p className="text-muted-foreground">
            The project "{project?.name}" has been permanently deleted from the system.
          </p>
        </div>
      );
    }

    if (isDeleting) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-red-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Deleting Project...</h3>
          <p className="text-muted-foreground">Permanently removing project from the system...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-muted-foreground">
              Are you sure you want to permanently delete the project <strong>{project?.name}</strong>?
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <span>Delete Project</span>
          </DialogTitle>
          <DialogDescription>
            This action will permanently delete the project and cannot be undone.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
             <div className="mt-4">
          {renderContent()}
        </div>
          {!isDeleting && !isComplete && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
             
                className={`rounded-lg ${
                 'bg-red-600 hover:bg-red-700 text-white' 
                }`}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Forever
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
